import { useEffect } from 'react'
import { TopNav, Footer } from '../components/PublicLayout'
import SEO from '../components/SEO'

// ─── Privacy Policy ──────────────────────────────────────────────────────────
//
// IMPORTANT: This is a reasonable starting template, not legal advice. Before
// launching to real customers, have a UK solicitor review it. UK GDPR carries
// significant fines for non-compliance, and this template can only cover the
// generic case — your specific operations may need different wording.
//
// Editable values — update these and the rest of the document picks them up.
const LAST_UPDATED  = '18 May 2026'
const CONTACT_EMAIL = 'hello@receiptchaser.com'
const TRADING_NAME  = 'Waterman Bookkeeping'

// ─── Page-section components ─────────────────────────────────────────────────

function Section({ number, title, children }) {
  return (
    <section style={{ marginBottom: 36 }}>
      <h2 style={{
        fontSize: '1.25rem', fontFamily: 'var(--font-display)',
        fontWeight: 400, marginBottom: 14, lineHeight: 1.3,
      }}>
        {number}. {title}
      </h2>
      <div style={{ fontSize: '0.95rem', color: 'var(--ink-muted)', lineHeight: 1.7 }}>
        {children}
      </div>
    </section>
  )
}

function P({ children }) {
  return <p style={{ marginBottom: 12 }}>{children}</p>
}

function UL({ children }) {
  return (
    <ul style={{
      paddingLeft: 22, marginBottom: 12,
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      {children}
    </ul>
  )
}

function SubHeading({ children }) {
  return (
    <h3 style={{
      fontSize: '1rem', fontWeight: 600,
      color: 'var(--ink)', marginTop: 18, marginBottom: 8,
    }}>
      {children}
    </h3>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function Privacy() {
  useEffect(() => { window.scrollTo(0, 0) }, [])

  return (
    <div style={{ background: 'var(--surface)', minHeight: '100vh' }}>
      <SEO
        title="Privacy policy"
        description="How Receipt Chaser handles personal data under the UK GDPR. The data we collect, why we collect it, how we keep it secure, and your rights as a data subject."
        path="/privacy"
      />
      <TopNav />

      <main style={{ padding: '48px 0 64px' }}>
        <div className="container" style={{
          padding: '0 24px', maxWidth: 760, margin: '0 auto',
        }}>
          <header style={{ marginBottom: 40 }}>
            <h1 style={{
              fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
              marginBottom: 8, lineHeight: 1.2,
            }}>
              Privacy Policy
            </h1>
            <p style={{ color: 'var(--ink-faint)', fontSize: '0.85rem' }}>
              Last updated: {LAST_UPDATED}
            </p>
          </header>

          <div style={{
            background: 'var(--white)', borderRadius: 'var(--radius-lg)',
            padding: 'clamp(28px, 5vw, 48px)',
            border: '1px solid var(--surface-2)',
          }}>

            <Section number="1" title="A note on how this policy works">
              <P>
                Receipt Chaser is a tool used by bookkeepers and accountants
                ("our customers") to collect receipts from their own clients
                ("end clients"). That means two different relationships exist
                here, and the law treats them differently:
              </P>
              <UL>
                <li>
                  <strong>For our bookkeeper customers</strong>, we are the
                  data controller. We decide what personal data we collect
                  about them, why, and how it's stored.
                </li>
                <li>
                  <strong>For the data those bookkeepers and their clients
                  upload into the service</strong> &mdash; receipts, bank
                  transactions, client lists, communications &mdash; we are
                  the data processor. Our bookkeeper customer is the
                  controller for that information. They decide what to
                  upload, what to do with it, and how long to keep it.
                </li>
              </UL>
              <P>
                This policy explains both. Sections 2 to 7 cover what we do
                with your data as a bookkeeper customer. Section 8 covers
                our role as processor for the data you upload about your
                clients.
              </P>
            </Section>

            <Section number="2" title="Who we are">
              <P>
                Receipt Chaser is operated by {TRADING_NAME} in the United
                Kingdom. You can contact us about anything in this policy at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
              </P>
              <P>
                For the purposes of UK GDPR, we are the data controller for
                the personal data of our bookkeeper customers (i.e. the
                person who signs up for an account on receiptchaser.com).
              </P>
            </Section>

            <Section number="3" title="What we collect about you">
              <P>
                When you create a Receipt Chaser account and use our service,
                we collect:
              </P>

              <SubHeading>Account details</SubHeading>
              <UL>
                <li>Your name and email address</li>
                <li>Your practice name and (optionally) phone number and website</li>
                <li>Your password (stored hashed &mdash; we never see it)</li>
                <li>Information about your subscription (plan, billing status)</li>
              </UL>

              <SubHeading>Information you upload</SubHeading>
              <UL>
                <li>Bank transaction descriptions and amounts</li>
                <li>Client names and email addresses</li>
                <li>Receipt files (photos and PDFs)</li>
                <li>Notes you and your clients write</li>
              </UL>
              <P>
                Most of this is information you upload about your clients
                rather than personal information about you. We cover our
                role as processor for that data in section 8.
              </P>

              <SubHeading>Usage information</SubHeading>
              <UL>
                <li>When you log in, what pages you visit, what actions you take</li>
                <li>Your IP address, browser type, and device information</li>
                <li>Information needed for security, fraud prevention, and debugging</li>
              </UL>

              <SubHeading>Payment information</SubHeading>
              <P>
                We don't store your card details. When you subscribe, your
                payment information is handled directly by our payment
                processor, Stripe. We receive enough information from Stripe
                to confirm payment (last four digits of your card, expiry,
                country) but not the full card number.
              </P>
            </Section>

            <Section number="4" title="Why we use it and the legal basis">
              <P>
                Under UK GDPR we have to have a "legal basis" for each thing
                we do with your personal data. Here's how that breaks down:
              </P>
              <UL>
                <li>
                  <strong>To provide the service</strong> (delivering the
                  features you signed up for): performance of a contract.
                </li>
                <li>
                  <strong>To take payment</strong>: performance of a
                  contract.
                </li>
                <li>
                  <strong>To send you essential account emails</strong>{' '}
                  (receipts, security alerts, important changes) and a
                  short welcome sequence of up to 3 emails when you first
                  sign in (one introductory email, one with support
                  articles, and one feedback check-in around day 7):
                  performance of a contract or legitimate interest.
                </li>
                <li>
                  <strong>To prevent fraud and keep the service secure</strong>:
                  legitimate interest.
                </li>
                <li>
                  <strong>To improve the service</strong> (analytics, bug
                  fixes): legitimate interest.
                </li>
                <li>
                  <strong>To send you marketing emails</strong> (if we ever
                  do this): your consent. You can withdraw consent at any
                  time.
                </li>
                <li>
                  <strong>To comply with the law</strong> (HMRC record-keeping,
                  responding to legal requests): legal obligation.
                </li>
              </UL>
            </Section>

            <Section number="5" title="Who we share it with">
              <P>
                We don't sell your data. We don't share your data for
                advertising. We share it only with the providers we need to
                run the service:
              </P>
              <UL>
                <li>
                  <strong>Supabase</strong> &mdash; hosts our database and
                  file storage. Your data is encrypted at rest and in
                  transit. Servers are in the EU.
                </li>
                <li>
                  <strong>Vercel</strong> &mdash; hosts the receiptchaser.com
                  website and app.
                </li>
                <li>
                  <strong>Stripe</strong> &mdash; processes payments. Stripe
                  is a UK-authorised payment institution.
                </li>
                <li>
                  <strong>Resend</strong> (or similar email provider) &mdash;
                  sends transactional emails (account confirmation, password
                  resets, notifications to your clients).
                </li>
              </UL>
              <P>
                Each of these providers acts as our data processor and is
                bound by data protection terms. If we ever change providers
                or add new ones, we'll update this list.
              </P>
              <P>
                We may also share data if required by law, by court order,
                or to protect our legal rights &mdash; for example, in
                response to an HMRC request or a fraud investigation.
              </P>
            </Section>

            <Section number="6" title="How long we keep it">
              <P>
                <strong>While your account is active</strong>, we
                keep each uploaded file for 7 years from the date
                that file was uploaded. This aligns with HMRC
                record-keeping requirements for UK businesses. The
                clock runs from the upload date itself, regardless
                of how long your subscription continues.
              </P>
              <P>
                <strong>When your access ends</strong> (subscription
                cancelled and paid period elapsed, or free trial
                elapsed without subscribing), we keep your account
                and data for a further 90 days so you can resubscribe
                if you change your mind. Roughly 7 days before
                deletion, we send a final warning email to the
                account's primary contact. After 90 days, the account
                and all its data are permanently deleted.
              </P>
              <P>
                You can ask us to delete your data sooner. Email{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and
                we'll comply, except where the law requires us to retain
                specific records (for example, financial records for tax
                purposes).
              </P>
              <P>
                If a file reaches its 7-year retention limit while
                your account is still active, we mark the related
                request as scheduled for deletion and hide it from
                view. The receipt files are permanently deleted 30
                days later. The transaction record (date, description,
                amount) is preserved as historical metadata.
              </P>
            </Section>

            <Section number="7" title="Your rights">
              <P>
                Under UK GDPR you have rights over your personal data,
                including:
              </P>
              <UL>
                <li>The right to access a copy of your data</li>
                <li>The right to correct anything that's wrong</li>
                <li>The right to ask us to delete your data</li>
                <li>The right to restrict or object to how we use it</li>
                <li>The right to receive your data in a portable format</li>
                <li>The right to withdraw consent (where consent was the legal basis)</li>
              </UL>
              <P>
                To exercise any of these rights, email{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We
                respond to requests within one month.
              </P>
              <P>
                If you're not happy with how we've handled a request, you can
                complain to the UK Information Commissioner's Office (ICO) at{' '}
                <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">
                  ico.org.uk
                </a>. We'd appreciate the chance to put things right first,
                so please contact us before going to the ICO.
              </P>
            </Section>

            <Section number="8" title="Data about your clients (our role as processor)">
              <P>
                When you upload your clients' bank statements, transactions,
                or receipts, you become the data controller for that
                information and we act as your data processor. This is the
                same arrangement you'll have with any other software you use
                in your bookkeeping &mdash; Xero, Sage, QuickBooks all work
                this way.
              </P>
              <P>
                As your processor, we will:
              </P>
              <UL>
                <li>Only process that data to provide the service to you</li>
                <li>Not use your clients' data for our own purposes</li>
                <li>Not pass it to other parties except as needed to deliver the service (see section 5)</li>
                <li>Keep it secure using industry-standard measures</li>
                <li>Help you respond to any data subject requests your clients make</li>
                <li>Notify you of any data breach affecting your account</li>
              </UL>
              <P>
                Your responsibilities as the controller for that data:
              </P>
              <UL>
                <li>Have a lawful basis for collecting and storing it (usually your engagement contract with your client)</li>
                <li>Tell your clients you use Receipt Chaser as part of your service</li>
                <li>Respond to any data subject requests your clients make</li>
                <li>Decide what to upload and what not to upload</li>
              </UL>
              <P>
                If you process special category data (health information,
                etc.) for your clients via Receipt Chaser, you must ensure
                you have a valid lawful basis under Article 9 of UK GDPR.
                We'd generally suggest not uploading anything like that
                &mdash; Receipt Chaser is built for financial transactions
                and receipts.
              </P>
            </Section>

            <Section number="9" title="Cookies and tracking">
              <P>
                We use cookies that are strictly necessary to run the
                service &mdash; for example, to keep you logged in and to
                process payments securely. These don't require your consent
                under UK law because the service can't work without them.
              </P>
              <P>
                We may also use non-essential cookies for analytics or
                advertising purposes. When we do, we'll show you a cookie
                banner asking for your consent before any non-essential
                cookies are set. You can change your preferences at any
                time using the cookie banner controls.
              </P>
              <P>
                Specific cookies we use:
              </P>
              <UL>
                <li>
                  <strong>Authentication cookies</strong> (essential):
                  keep you signed in. Set by our auth provider, Supabase.
                </li>
                <li>
                  <strong>Stripe cookies</strong> (essential): used by
                  Stripe to process payments and prevent fraud.
                </li>
                <li>
                  <strong>Analytics cookies</strong> (non-essential, only if
                  you consent): help us understand how the service is used.
                </li>
                <li>
                  <strong>Advertising cookies</strong> (non-essential, only
                  if you consent): if used, let us measure the performance
                  of ads we run on platforms like Google or Facebook.
                </li>
              </UL>
            </Section>

            <Section number="10" title="Children">
              <P>
                Receipt Chaser is a business tool not intended for use by
                children. We don't knowingly collect data about anyone under
                18. If you believe we have, please contact us and we'll
                delete it.
              </P>
            </Section>

            <Section number="11" title="International data transfers">
              <P>
                We try to keep data within the UK and EU. The hosting
                providers we use store data in the EU. Some of our service
                providers (such as Stripe) may transfer data to the US
                under approved transfer mechanisms (Standard Contractual
                Clauses, UK Addendum, or adequacy decisions). Where we use
                such providers, they've committed to UK GDPR-equivalent
                protections.
              </P>
            </Section>

            <Section number="12" title="Changes to this policy">
              <P>
                We may update this policy from time to time. If we make
                material changes, we'll let you know by email before they
                take effect and update the "Last updated" date at the top
                of this page.
              </P>
            </Section>

            <Section number="13" title="Getting in touch">
              <P>
                For any questions about this policy or about how we handle
                your data, email{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
              </P>
              <P>
                If you're unhappy with how we've handled your data, you can
                also contact the UK Information Commissioner's Office at{' '}
                <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">
                  ico.org.uk
                </a>.
              </P>
            </Section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}
